import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import AvatarGroup from "@mui/material/AvatarGroup";
import "./TemplatesTable.css";
import {Chip,Pagination,Stack,TablePagination,TableSortLabel,Tooltip,Typography} from "@mui/material";
import React, { useEffect, useState } from "react";
import DuplicateTemplateModal from "../TemplateModal/DuplicateTemplateModal";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
import { useLocation, useNavigate } from "react-router-dom";
import TemplatesFilterCard from "../TemplatesFilter/TemplatesFilterCard";
import IconButton from "@mui/material/IconButton";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Box } from "@mui/system";
import { useSelector } from "react-redux";
import { selectUserData } from "../../../../../features/userSlice";
import { CheckPermissionsDesign } from "../../../../../pages/AssetManagement/Design/CheckPermission/CheckDesignPermissions";
import store from "../../../../../store";
import {
  setSelectedTemplateShareuid,
  setPage,
} from "../../../../../features/templateSlice";
import { useSearchParams } from "react-router-dom";
import { formatDate } from '../../../../../utils/helpers';

interface Props {
  tableHeadings: { [key: string]: string };
  setLoading: any;
  loading: any;
  setSelectedSharedUid: any;
}
function TemplatesTable({ loading }: Props) {
  const { designTemplatesData, page, selectedTemplate } = useSelector(
    (state: any) => state.template
  );
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [searchParams, setSearchParams] = useSearchParams();

  const [openModal, setOpenModal] = useState(false);
  const [sort, setSort] = useState<{
    keytoSort: string | undefined;
    direction: "asc" | "desc" | undefined;
  }>({
    keytoSort: "modified",
    direction: "desc",
  });
  const userData = useSelector(selectUserData);
  const hasEditDesignPermission = CheckPermissionsDesign(
    userData?.data?.products,
    "edit_template"
  );
  const hasDuplicateDesignPermission = CheckPermissionsDesign(
    userData?.data?.products,
    "duplicate_template"
  );

  const { selectedFeatures, selectedComponents } = useSelector(
    (state: any) => state.template
  );

  const handleChangePage = (_: unknown, newPage: number) => {
    store.dispatch(setPage(newPage));

    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("page", newPage.toString());
      return updatedParams;
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    store.dispatch(setPage(0));
  };

  const handleOpenModal = (shared_uid: any) => {
    store.dispatch(setSelectedTemplateShareuid(shared_uid)); // Set the selected template's shared_uid
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const filteredData =
    designTemplatesData &&
    designTemplatesData?.filter(
      (template: any) =>
        !selectedTemplate || template?.name === selectedTemplate
    );

  const filteredTemplateData = filteredData.filter((template: any) => {
    const statusComponents =
      selectedComponents && selectedComponents.length !== 0
        ? selectedComponents.includes(template?.component)
        : true; // If no Asset is selected, treat it as a match

    const statusFeatures =
      selectedFeatures && selectedFeatures?.length !== 0
        ? selectedFeatures.some((group: any) =>
            template?.labels?.includes(group)
          )
        : true; // If no Asset is selected, treat it as a match

    return statusComponents && statusFeatures;
  });

  useEffect(() => {
    const pageFromUrl = Number(searchParams.get("page")); // Convert to number
    store.dispatch(setPage(isNaN(pageFromUrl) ? 0 : pageFromUrl)); // Ensure it defaults correctly
  }, [searchParams]);

  useEffect(() => {
    if (page !== 0) {
      store.dispatch(setPage(0));
    }
    // eslint-disable-next-line
  }, [selectedFeatures, selectedComponents, selectedTemplate && page]);

  // headers for the table columns with sortable option for each column
  const tableheaders = [
    { id: 0, KEY: "name", label: "Name", sortable: true },
    { id: 1, KEY: "component", label: "Component", sortable: true },
    { id: 2, KEY: "modified", label: "Last Updated", sortable: true },
    { id: 3, KEY: "labels", label: "Features", sortable: true },
    { id: 4, KEY: "quick_actions", label: "Quick Actions", sortable: false },
  ];

  // function to handle click on table headers
  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable

    setSort({
      keytoSort: header.KEY,
      // Toggle sort direction if the same column is clicked, otherwise default to ascending
      direction:
        header.KEY === sort.keytoSort && sort.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getSortedData = (arrayToSort: any[]) => {
    // If no sorting key is set, return the original array
    if (!sort.keytoSort || !sort.direction) {
      return arrayToSort;
    }

    // Clone the array to avoid mutating the original array
    return arrayToSort.slice().sort((a, b) => {
      const keytoSort = sort.keytoSort;

      if (keytoSort === undefined) {
        return 0; // Return 0 if the sorting key is undefined
      }

      const aValue = a[keytoSort];
      const bValue = b[keytoSort];

      // Handle undefined values for sorting
      if (aValue === undefined) {
        return 1; // Move undefined values to the end
      }
      if (bValue === undefined) {
        return -1; // Move undefined values to the end
      }

      // Handle "N/A" values for sorting
      if (aValue === "") {
        return 1; // Move "N/A" values to the end
      }
      if (bValue === "") {
        return -1; // Move "N/A" values to the end
      }

      // Custom sorting for labels
      if (keytoSort === "labels") {
        // Sort based on the length of labels array first
        const aLabelCount = Array.isArray(aValue) ? aValue.length : 0;
        const bLabelCount = Array.isArray(bValue) ? bValue.length : 0;

        // For descending order, empty labels come first
        if (sort.direction === "desc") {
          if (aLabelCount === 0 && bLabelCount > 0) return -1; // a is empty, b is not
          if (bLabelCount === 0 && aLabelCount > 0) return 1; // b is empty, a is not
        } else {
          // For ascending order, empty labels come last
          if (aLabelCount === 0 && bLabelCount > 0) return 1; // a is empty, b is not
          if (bLabelCount === 0 && aLabelCount > 0) return -1; // b is empty, a is not
        }
      }

      // Perform sorting based on the direction and type of values
      if (typeof aValue === "string" && typeof bValue === "string") {
        // String comparison (localeCompare for correct string sorting)
        return sort.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else if (typeof aValue === "number" && typeof bValue === "number") {
        // Numeric comparison
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      } else if (aValue instanceof Date && bValue instanceof Date) {
        // Date comparison
        return sort.direction === "asc"
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      } else {
        // Fallback to string comparison if values are of different types
        return sort.direction === "asc"
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }
    });
  };

  const sortedRows = getSortedData(filteredTemplateData || []);
  const paginatedTemplates = sortedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const location = useLocation();
  const navigate = useNavigate();

  const handleClick = (row: any) => {
    const params = new URLSearchParams(location.search);
    params.set("templateSharedUid", row?.shared_uid); // Add new param while keeping others
    params.set("page", "0"); // Reset page to 0

    navigate(`edit_new_template?${params.toString()}`);
  };

  return (
    <>
      {loading ? (
        <SimpleBackdrop />
      ) : (
        <Paper sx={{ marginTop: "8px" }}>
          <TemplatesFilterCard />
          <TableContainer
            className="templatesWrap"
            component={Paper}
            sx={{ marginTop: "16px", boxShadow: "none" }}
          >
            <Table aria-label="collapsible table" size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {tableheaders.map((header) => (
                    <TableCell key={header.id}>
                      <TableSortLabel
                        active={sort.keytoSort === header.KEY}
                        direction={
                          sort.keytoSort === header.KEY ? sort.direction : "asc"
                        }
                        onClick={() => handleHeaderClick(header)}
                        disabled={!header.sortable}
                      >
                        {header.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTemplates.length > 0 && paginatedTemplates ? (
                  paginatedTemplates?.map((row: any, index: number) => (
                    <React.Fragment key={index}>
                      <TableRow
                        sx={{
                          "& > *": {
                            borderBottom:
                              "1px solid rgba(224, 224, 224, 1) !important",
                          },
                        }}
                      >
                        <TableCell align="left">{row.name}</TableCell>
                        <TableCell align="left">{row.component}</TableCell>
                        <TableCell align="left">
                          {formatDate(row.modified)}
                        </TableCell>
                        <TableCell align="left">
                          <div className="chip-data">
                            {Array.isArray(row.labels) &&
                            row.labels.length > 0 ? (
                              <>
                                <Chip
                                  label={row?.labels[0]} // Display the first label
                                  sx={{
                                    backgroundColor: "#7B1FA2",
                                    color: "#ffffff",
                                    height: "26px",
                                  }}
                                />
                                {row.labels.length > 1 && ( // Check if there are additional labels
                                  <AvatarGroup
                                    total={row?.labels?.length - 1}
                                    variant="circular"
                                  ></AvatarGroup>
                                )}
                              </>
                            ) : null}{" "}
                            {/* Render nothing if there are no labels */}
                          </div>
                        </TableCell>
                        <TableCell align="left">
                          <>
                            <Box display="flex" gap={1}>
                              <Tooltip title="Edit" placement="top">
                                <Box>
                                  {hasEditDesignPermission ? (
                                    <IconButton
                                      size="small"
                                      data-tracker="152"
                                      onClick={() => handleClick(row)}
                                    >
                                      <CreateOutlinedIcon
                                        fontSize="small"
                                        color="secondary"
                                      />
                                    </IconButton>
                                  ) : (
                                    <IconButton size="small">
                                      <CreateOutlinedIcon
                                        fontSize="small"
                                        color="disabled"
                                      />
                                    </IconButton>
                                  )}
                                </Box>
                              </Tooltip>
                              <Tooltip title="Duplicate" placement="top">
                                {hasDuplicateDesignPermission ? (
                                  <IconButton
                                    size="small"
                                    data-tracker="150"
                                    onClick={() =>
                                      handleOpenModal(row?.shared_uid)
                                    }
                                  >
                                    <ContentCopyOutlinedIcon
                                      fontSize="small"
                                      color="secondary"
                                    />
                                  </IconButton>
                                ) : (
                                  <IconButton size="small" disabled>
                                    <ContentCopyOutlinedIcon
                                      fontSize="small"
                                      color="disabled"
                                    />
                                  </IconButton>
                                )}
                              </Tooltip>
                              <Tooltip title="Delete" placement="top">
                                <IconButton
                                  size="small"
                                  disabled
                                  color="primary"
                                >
                                  <DeleteOutlineOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body1">No Data Available</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Stack
              spacing={1}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <TablePagination
                rowsPerPageOptions={[]}
                component="div"
                count={sortedRows?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },
                  ".MuiTablePagination-actions": {
                    display: "none",
                  },
                  ".MuiTablePagination-toolbar": {
                    paddingLeft: "16px",
                  },
                }}
              />
              <Pagination
                count={Math.ceil(filteredTemplateData?.length / rowsPerPage)}
                showFirstButton
                showLastButton
                className="pagination-button"
                color="secondary"
                size="small"
                sx={{
                  ".css-wjh20t-MuiPagination-ul": {
                    justifyContent: "center",
                  },
                }}
                onChange={(_, value) => handleChangePage(null, value - 1)}
                page={page + 1}
              />
              <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={sortedRows?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                classes={{
                  displayedRows: "hidden-displayed-rows", // Apply the custom class
                }}
                sx={{
                  ".MuiTablePagination-actions": {
                    display: "none",
                  },
                  ".MuiTablePagination-input": {
                    marginRight: "15px",
                  },
                }}
              />
            </Stack>
            <DuplicateTemplateModal
              open={openModal}
              onClose={handleCloseModal}
            />
          </TableContainer>
        </Paper>
      )}
    </>
  );
}
export default TemplatesTable;
